/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect } from 'react'

import type { EuiTabbedContentTab } from '@elastic/eui'
import { EuiSkeletonText, EuiSpacer, EuiTabbedContent } from '@elastic/eui'

import { CuiAlert } from '@modules/cui/Alert'
import Header from '@modules/cui/Header'
import history from '@modules/utils/history'

import { viewOrganizationCrumbs } from '@/lib/crumbBuilder'

import FeatureUsageBanner from '../Billing/FeatureUsageBanner'
import BillingStatus from '../Billing/BillingStatus'

import useOrganizationContent from './hooks/useOrganizationContent'

import type { FunctionComponent } from 'react'
import type { AllProps as Props } from './types'

type OrganizationTab = EuiTabbedContentTab & { route: string }

const Organization: FunctionComponent<Props> = ({
  ccmFeature,
  fetchOrganizationInvites,
  fetchOrganizationMembers,
  fetchOrganizationMembersRequest,
  fetchSaasOrganization,
  fetchOrganizationInvitationsRequest,
  fetchSaasOrganizationRequest,
  lookupSaasUsers,
  organizationMemberRows,
  saasOrganization,
  searchResults,
  selectedTabId,
  serverlessFeature,
}) => {
  const { tabs, selectedTab, NonTabbedContent } = useOrganizationContent({
    ccmFeature,
    fetchOrganizationInvitationsRequest,
    fetchOrganizationMembersRequest,
    organizationMemberRows,
    saasOrganization,
    searchResults,
    selectedTabId,
    serverlessFeature,
  })

  const hasTabbedContent = !NonTabbedContent

  useEffect(() => {
    if (lookupSaasUsers && !saasOrganization) {
      fetchSaasOrganization()
      fetchOrganizationMembers()
      fetchOrganizationInvites()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { error } = fetchSaasOrganizationRequest

  if (!lookupSaasUsers) {
    return null
  }

  if (error) {
    return <CuiAlert type='error'>{error}</CuiAlert>
  }

  if (!saasOrganization) {
    return <EuiSkeletonText />
  }

  const {
    organization: { organization_id: organizationId, organization_name: organizationName },
  } = saasOrganization

  return (
    <Fragment>
      <BillingStatus />
      <FeatureUsageBanner />
      <EuiSpacer size='m' />
      <Header
        dataTestId='organization-header'
        name={hasTabbedContent && organizationName}
        breadcrumbs={viewOrganizationCrumbs(organizationId, organizationName)}
        bottomBorder={false}
      />

      {NonTabbedContent ? (
        <NonTabbedContent />
      ) : (
        <EuiTabbedContent
          tabs={tabs}
          selectedTab={selectedTab}
          onTabClick={(selected: OrganizationTab) => history.replace(selected.route)}
        />
      )}

      <EuiSpacer size='xl' />
    </Fragment>
  )
}

export default Organization
